/**
 * WebSocket-Client.
 *
 * Der Server schickt nur Referenzen, keine Inhalte (ADR-005). Ein Ereignis ist
 * also immer ein Anstoß, etwas nachzuladen — nie die Nutzlast selbst. Das ist
 * kein Umweg: Es stellt sicher, dass jede Anzeige durch die Berechtigungsprüfung
 * der API gelaufen ist.
 */

export interface ServerEvent {
  type: string
  familyId: string
  roomId?: string
  personId?: string
  ref?: Record<string, string | number | boolean | null>
}

type Listener = (event: ServerEvent) => void

const listeners = new Set<Listener>()
let socket: WebSocket | null = null
let versuche = 0
let absichtlichZu = false
let reconnectTimer: number | undefined

export function onEvent(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Für den Verbindungsindikator in der Oberfläche. */
export type WsState = 'connecting' | 'open' | 'closed'
let state: WsState = 'closed'
const stateListeners = new Set<(s: WsState) => void>()

export function onWsState(listener: (s: WsState) => void): () => void {
  stateListeners.add(listener)
  listener(state)
  return () => stateListeners.delete(listener)
}

function setState(next: WsState): void {
  state = next
  for (const l of stateListeners) l(next)
}

/**
 * Wird beim Wiederverbinden aufgerufen — mit dem letzten bekannten Stand.
 *
 * Der WebSocket meldet nur, was passiert, solange er steht. Nach einem
 * Abbruch fehlt alles dazwischen; genau diese Lücke schließt der Delta-Sync
 * (Anf. 30).
 */
const reconnectListener = new Set<() => void>()

export function onReconnect(listener: () => void): () => void {
  reconnectListener.add(listener)
  return () => reconnectListener.delete(listener)
}

export function connect(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }
  absichtlichZu = false
  setState('connecting')

  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`
  socket = new WebSocket(url)

  socket.addEventListener('open', () => {
    // Nur bei einem WIEDERaufbau nachsynchronisieren, nicht beim ersten
    // Verbinden — dort lädt die App ohnehin gerade alles.
    const warAbbruch = versuche > 0
    versuche = 0
    setState('open')
    if (warAbbruch) for (const l of reconnectListener) l()
  })

  socket.addEventListener('message', (e) => {
    let event: ServerEvent
    try {
      event = JSON.parse(String(e.data)) as ServerEvent
    } catch {
      return
    }
    for (const l of listeners) l(event)
  })

  socket.addEventListener('close', () => {
    setState('closed')
    if (absichtlichZu) return
    // Exponentiell mit Jitter: Nach einem Serverneustart sollen nicht alle
    // Geräte der Familie gleichzeitig wieder anklopfen.
    versuche += 1
    const basis = Math.min(1000 * 2 ** Math.min(versuche, 5), 30_000)
    const wartezeit = basis / 2 + Math.random() * (basis / 2)
    reconnectTimer = self.setTimeout(connect, wartezeit)
  })

  socket.addEventListener('error', () => { socket?.close() })
}

export function disconnect(): void {
  absichtlichZu = true
  clearTimeout(reconnectTimer)
  socket?.close()
  socket = null
  setState('closed')
}

/** Tippanzeige. Wird verworfen, wenn keine Verbindung steht — das ist in Ordnung. */
export function sendeTippt(roomId: string): void {
  if (socket?.readyState !== WebSocket.OPEN) return
  socket.send(JSON.stringify({ type: 'typing', roomId }))
}
